import { Box, TextInput, type TextInputProps } from "@mantine/core";
import { IconAlertOctagon } from "@tabler/icons-react";
import clsx from "clsx";
import { useId } from "react";
import { Controller, type Control } from "react-hook-form";
import styles from "./Input.module.scss";

export type InputSize = "small" | "large";

function FormattedTextInput({
  field,
  fieldRef,
  dataTestId,
  id,
  type,
  step,
  placeholder,
  disabled,
  autoComplete,
  iconLeft,
  leftWidth,
  iconRight,
  rightWidth,
  style,
  size,
  error,
  inputElementWidth,
  onChange,
  maxNumericValue,
  ...rest
}: {
  field: any;
  fieldRef?: React.Ref<HTMLInputElement>;
  dataTestId?: string;
  id: string;
  type?: string;
  step?: string | number;
  placeholder: string;
  disabled?: boolean;
  autoComplete?: string;
  iconLeft?: React.ReactNode;
  leftWidth?: number;
  iconRight?: React.ReactNode;
  rightWidth?: number;
  style?: React.CSSProperties;
  size: InputSize;
  error?: boolean | string;
  inputElementWidth: string | number;
  onChange?: (val: string) => void;
  maxNumericValue?: number;
  [key: string]: any;
}) {
  return (
    <TextInput
      data-testid={dataTestId}
      id={id}
      ref={fieldRef}
      name={field.name}
      value={field.value ?? ""}
      onChange={(e) => {
        const raw = e.currentTarget.value;
        let nextValue = raw;
        const inputModeValue = rest.inputMode as string | undefined;
        if (
          (inputModeValue === "numeric" || inputModeValue === "decimal") &&
          nextValue !== "" &&
          !/^\d*\.?\d*$/.test(nextValue)
        ) {
          return;
        }
        if (maxNumericValue !== undefined && nextValue !== "") {
          const num = parseFloat(nextValue);
          if (!isNaN(num) && num > maxNumericValue) {
            nextValue = String(maxNumericValue);
          }
        }
        field.onChange(nextValue);
        onChange?.(nextValue);
      }}
      // formatted inputs must be text, not number
      type={type}
      step={step}
      onKeyDown={(e) => {
        if (type === "number" && (e.key === "e" || e.key === "E")) {
          e.preventDefault();
        }
      }}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete={autoComplete}
      leftSection={iconLeft}
      leftSectionWidth={iconLeft ? leftWidth : undefined}
      rightSection={iconRight}
      rightSectionWidth={iconRight ? rightWidth : undefined}
      style={style}
      classNames={{
        input: clsx(styles.input, styles[`input-${size}`], error && styles.inputError),
      }}
      w={inputElementWidth}
      onWheel={
        type === "number"
          ? (e: React.WheelEvent<HTMLInputElement>) => {
              // Prevent the input value change
              (e.target as HTMLInputElement).blur();

              // Prevent the page/container scrolling
              e.stopPropagation();

              // Refocus the input
              setTimeout(() => {
                (e.target as HTMLInputElement).focus();
              }, 0);
            }
          : undefined
      }
      {...rest}
    />
  );
}

export type ControllerInputProps = Omit<
  TextInputProps,
  "value" | "onChange" | "error" | "type" | "placeholder" | "disabled"
> & {
  label?: string;
  optional?: boolean;
  hint?: string;
  helpIcon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  size?: InputSize;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  value?: string;
  onChange?: (val: string) => void;
  error?: boolean | string;
  type?: string;
  control?: Control<any>;
  name: string;
  className?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
  width?: string | number; // Input wrapper width
  inputElementWidth?: string | number; // Input html element width
  autoComplete?: string;
  step?: string | number; // Step attribute for number inputs
  maxNumericValue?: number;
};

export function ControllerInput({
  label,
  optional,
  hint,
  helpIcon,
  placeholder = "Enter",
  disabled,
  size = "small",
  iconLeft,
  iconRight,
  value: _value,
  onChange,
  error,
  control,
  name,
  type,
  className,
  style,
  dataTestId,
  width,
  inputElementWidth = "100%",
  autoComplete,
  step,
  maxNumericValue,
  ...rest
}: ControllerInputProps) {
  const id = useId();

  const iconSize = size === "large" ? 24 : 16;
  const leftWidth = 16 + iconSize;
  const rightWidth = iconRight ? 16 + iconSize : undefined;

  return (
    <Box
      className={clsx(styles.wrapper, styles[`wrapper-${size}`], className)}
      w={width}
      style={{ minWidth: style?.minWidth, maxWidth: style?.maxWidth }}
    >
      {(label || optional) && (
        <Box className={styles.labelRow}>
          {label && (
            <label htmlFor={id} className={styles.labelText}>
              {label}
              {rest.required && <span style={{ color: "var(--text-danger-default)" }}> *</span>}
              {helpIcon && <span className={styles.helpIcon}>{helpIcon}</span>}
            </label>
          )}
          {optional && <span className={styles.optionalText}>Optional</span>}
        </Box>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const resolvedError =
            fieldState.error?.message || (typeof error === "string" ? error : undefined);
          return (
            <>
              <FormattedTextInput
                field={field}
                fieldRef={field.ref}
                dataTestId={dataTestId}
                id={id}
                type={type}
                step={step}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete={autoComplete}
                iconLeft={iconLeft}
                leftWidth={leftWidth}
                iconRight={iconRight}
                rightWidth={rightWidth}
                style={style}
                size={size}
                error={resolvedError || error}
                inputElementWidth={inputElementWidth}
                onChange={onChange}
                maxNumericValue={maxNumericValue}
                {...rest}
              />
              {resolvedError && (
                <Box className={styles.errorMessage}>
                  <IconAlertOctagon size={16} className={styles.errorIcon} />
                  <span>{resolvedError}</span>
                </Box>
              )}
            </>
          );
        }}
      />

      {hint && <Box className={styles.hint}>{hint}</Box>}
    </Box>
  );
}
